"use client"

import { motion } from "motion/react"
import { ArrowRight } from "lucide-react"
import dynamic from 'next/dynamic'
import Link from "next/link"

import { Button } from "@/core/ui/button"


// Dynamically import 3D components to avoid SSR issues and improve initial load
const BlockchainMesh = dynamic(() => import("@/core/ui/effects/BlockchainMesh"), { ssr: false })
const NeuralBrain = dynamic(() => import("@/core/ui/effects/NeuralBrain"), { ssr: false })
const IsoGrid = dynamic(() => import("@/core/ui/effects/IsoGrid"), { ssr: false })
const GlobeNetwork = dynamic(() => import("@/core/ui/effects/GlobeNetwork"), { ssr: false })

interface CategoryHeroProps {
  title: string
  description: string
  icon?: string
  variant?: "blockchain" | "genai" | "development" | "enterprise" | string
}

const themeConfig: Record<string, {
  hex: string;
  badge: string;
  badgeDot: string;
  descriptionBorder: string;
  decorativeGradient: string;
}> = {
  blockchain: {
    hex: "#06b6d4",
    badge: "border-cyan-500/30 bg-cyan-500/5 text-cyan-400",
    badgeDot: "bg-cyan-400",
    descriptionBorder: "border-cyan-500/20",
    decorativeGradient: "via-cyan-500/30",
  },
  genai: {
    hex: "#a855f7",
    badge: "border-purple-500/30 bg-purple-500/5 text-purple-400",
    badgeDot: "bg-purple-400",
    descriptionBorder: "border-purple-500/20",
    decorativeGradient: "via-purple-500/30",
  },
  enterprise: {
    hex: "#3b82f6",
    badge: "border-blue-500/30 bg-blue-500/5 text-blue-400",
    badgeDot: "bg-blue-400",
    descriptionBorder: "border-blue-500/20",
    decorativeGradient: "via-blue-500/30",
  },
  development: {
    hex: "#10b981",
    badge: "border-emerald-500/30 bg-emerald-500/5 text-emerald-400",
    badgeDot: "bg-emerald-400",
    descriptionBorder: "border-emerald-500/20",
    decorativeGradient: "via-emerald-500/30",
  },
  default: {
    hex: "#10b981",
    badge: "border-emerald-500/30 bg-emerald-500/5 text-emerald-400",
    badgeDot: "bg-emerald-400",
    descriptionBorder: "border-emerald-500/20",
    decorativeGradient: "via-emerald-500/30",
  }
}

export default function CategoryHero({ title, description, icon: _icon, variant = "development" }: CategoryHeroProps) {

  const theme = themeConfig[variant] || themeConfig.default

  const renderBackground = () => {
    switch (variant) {
      case "blockchain":
        return <BlockchainMesh color={theme.hex} />
      case "genai":
        return <NeuralBrain color={theme.hex} />
      case "enterprise":
        return <GlobeNetwork color={theme.hex} />
      case "development":
      default:
        return <IsoGrid color={theme.hex} />
    }
  }

  return (
    <div className="relative min-h-[85vh] w-full flex items-center overflow-hidden bg-background border-b border-border/20 transition-colors duration-300">
      {/* Background Layer - Increased visibility */}
      <div className="absolute inset-0 z-0 opacity-100">
        {renderBackground()}
      </div>

      {/* Gradient Overlay - Theme aware */}
      <div className="absolute inset-0 z-1 bg-linear-to-b from-background/10 via-background/40 to-background pointer-events-none" />

      {/* Content Layer */}
      <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-16 pt-32 md:pt-40">
        <div className="max-w-4xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mb-8 inline-flex"
          >
            <span className={`inline-flex items-center px-4 py-1.5 rounded border backdrop-blur-md text-xs font-mono tracking-[0.2em] uppercase ${theme.badge}`}>
              <span className={`w-2 h-2 rounded-full mr-3 animate-pulse ${theme.badgeDot}`} />
              Protocol: {title.replace(/\s+/g, '_')}
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-foreground mb-8 leading-[0.9]"
          >
            <span className="block text-transparent bg-clip-text bg-linear-to-r from-foreground via-foreground to-foreground/50">
              {title}
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className={`text-lg md:text-xl text-muted-foreground max-w-2xl mb-12 leading-relaxed border-l-2 pl-6 ${theme.descriptionBorder}`}
          >
            {description}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-wrap gap-6 items-center"
          >
            <Button asChild size="lg" className="rounded-full px-8 h-14 text-base font-medium bg-foreground text-background hover:bg-muted-foreground/20 hover:text-foreground transition-all duration-300 shadow-[0_0_20px_rgba(0,0,0,0.1)] dark:shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(0,0,0,0.2)] dark:hover:shadow-[0_0_30px_rgba(255,255,255,0.5)]">
              <Link href="#capabilities">
                Initialize Modules
              </Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="rounded-full px-8 h-14 text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors border border-border/20 hover:border-foreground/20">
              <Link href="/contact" className="flex items-center gap-2">
                Start Project <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-px h-32 bg-linear-to-b from-transparent ${theme.decorativeGradient} to-transparent hidden lg:block`} />
      <div className="absolute right-8 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted-foreground/20 hidden lg:flex flex-col gap-8 writing-mode-vertical tracking-widest">
        <span>SYS_READY</span>
        <span>SECURE_CONN</span>
      </div>
    </div>
  )
}
